import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/layout";
import Button from "../components/button";
import Modal from "../components/modal";
import projects, { getProjectById } from "../data/projects";

export default function WorkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(null);
  const selectedProject = getProjectById(selectedId);

  const selectedIndex = selectedId
    ? projects.findIndex((p) => p.id === selectedId)
    : -1;

  useEffect(() => {
    const projectFromUrl = searchParams.get("project");
    if (!projectFromUrl) return;
    if (!getProjectById(projectFromUrl)) return;
    setSelectedId(projectFromUrl);
  }, [searchParams]);

  const openProject = (projectId) => {
    setSelectedId(projectId);
    setSearchParams({ project: projectId });
  };

  const closeProject = () => {
    setSelectedId(null);
    if (!searchParams.get("project")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("project");
    setSearchParams(next, { replace: true });
  };

  const goToProject = useCallback(
    (direction) => {
      if (selectedIndex < 0) return;
      const total = projects.length;
      const nextIndex =
        direction === "next"
          ? (selectedIndex + 1) % total
          : (selectedIndex - 1 + total) % total;
      const nextProject = projects[nextIndex];
      setSelectedId(nextProject.id);
      setSearchParams({ project: nextProject.id }, { replace: true });
      // Scroll modal back to very top (including hero)
      const modalMain = document.querySelector(".modal-main");
      if (modalMain) {
        modalMain.scrollTo({ top: 0, behavior: "instant" });
      }
    },
    [selectedIndex, setSearchParams]
  );

  const prevProject =
    selectedIndex >= 0
      ? projects[(selectedIndex - 1 + projects.length) % projects.length]
      : null;
  const nextProject =
    selectedIndex >= 0
      ? projects[(selectedIndex + 1) % projects.length]
      : null;

  return (
    <Layout>
      <div className="container py-5">
        <div className="work-header">
          <h1 className="mb-3">Our Work</h1>
          <p className="text-muted mb-4">
            A selection of projects across branding, web, and design. Click any
            project to see a deeper breakdown.
          </p>
          <Button to="/contact" type="cta">
            Start a project
          </Button>
        </div>

        <div className="work-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`work-card-wrap work-card-wrap--${project.outlineStyle || "black"}`}
            >
              <button
                type="button"
                className="work-card"
                onClick={() => openProject(project.id)}
                style={{ backgroundImage: `url(${project.heroImage})` }}
              >
                <div className="work-card__overlay" />
                <div className="work-card__content">
                  <div className="work-card__category">{project.category}</div>
                  <div className="work-card__title">{project.title}</div>
                  <div className="work-card__cta">View project</div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      <Modal show={Boolean(selectedProject)} handleClose={closeProject}>
        {selectedProject ? (
          <div className="work-modal">
            <div
              className="work-modal__hero"
              style={{ backgroundImage: `url(${selectedProject.heroImage})` }}
            >
              <div className="work-modal__heroOverlay" />
              <div className="work-modal__heroInner">
                <div className="work-modal__category">{selectedProject.category}</div>
                <h2 className="work-modal__title">{selectedProject.title}</h2>
                <p className="work-modal__summary">{selectedProject.summary}</p>
              </div>
            </div>

            <div className="work-modal__body">
              <div className="work-modal__section">
                <h3>Services</h3>
                <ul>
                  {selectedProject.services.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>

              <div className="work-modal__section">
                <h3>Outcomes</h3>
                <ul>
                  {selectedProject.outcomes.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ul>
              </div>

              {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                <div className="work-modal__section work-modal__gallery-section">
                  <h3>Project work</h3>
                  <div className="work-modal__gallery">
                    {selectedProject.gallery.map((img, i) => (
                      <div
                        key={i}
                        className={`work-modal__gallery-item${img.wide ? " work-modal__gallery-item--wide" : ""}`}
                      >
                        <img src={img.src} alt={img.alt} loading="lazy" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prev / Next navigation */}
              <div className="work-modal__nav">
                <button
                  type="button"
                  className="work-modal__nav-btn"
                  onClick={() => goToProject("prev")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  <span className="work-modal__nav-label">
                    {prevProject && prevProject.title}
                  </span>
                </button>
                <button
                  type="button"
                  className="work-modal__nav-btn work-modal__nav-btn--next"
                  onClick={() => goToProject("next")}
                >
                  <span className="work-modal__nav-label">
                    {nextProject && nextProject.title}
                  </span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>

              <div className="work-modal__footer">
                <Button to="/contact">Work with us</Button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </Layout>
  );
}
